import React, { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useActiveClinic } from "../../../context/ClinicContext"
import {
  planButtonStyle as buttonStyle,
  StatusBadge
} from "../components/PlanPrimitives"
import PlanModuleHero from "../components/PlanModuleHero"
import PlanQueueSection from "../components/PlanQueueSection"
import PlanDraftBuilder from "../components/PlanDraftBuilder"
import PlanQueueMobileList from "../components/PlanQueueMobileList"
import {
  getPendingBalance,
  findNextSchedulableItem,
  getPlanPatientName
} from "../utils/planQueuePresentation"
import {
  buildPatientFocusedMetrics,
  buildPlansSummary,
  buildExecutiveInsights
} from "../utils/planQueueInsights"
import { buildFilteredPlans } from "../utils/planQueueFilters"
import useTreatmentPlanNavigation from "../hooks/useTreatmentPlanNavigation"
import useTreatmentPlanDraft from "../hooks/useTreatmentPlanDraft"
import useTreatmentPlansData from "../hooks/useTreatmentPlansData"

function formatMoney(value) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0
  }).format(Number(value || 0))
}

function formatDateTime(value) {
  if (!value) return "-"
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return "-"
  return d.toLocaleString("es-MX", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  })
}

function buildNextAction(plan) {
  const nextItem = findNextSchedulableItem(plan)
  const pendingBalance = getPendingBalance(plan)
  const status = String(plan?.status || "").toUpperCase()

  if (status === "COMPLETED") {
    return {
      title: "Plan completado",
      helper: "Sin acciones pendientes"
    }
  }

  if (nextItem) {
    return {
      title: nextItem.treatment?.name || "Agendar siguiente",
      helper: "Hay un ítem listo para programar"
    }
  }

  if (pendingBalance > 0) {
    return {
      title: "Dar seguimiento a saldo",
      helper: "Aún existe cobranza pendiente"
    }
  }

  return {
    title: "Revisar plan",
    helper: "Sin acción inmediata"
  }
}

export default function TreatmentPlansPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { activeClinicId } = useActiveClinic()

  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440
  )

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const patientIdFromUrl = queryParams.get("patientId") || ""
  const clinicIdFromUrl = queryParams.get("clinicId") || ""
  const qFromUrl = queryParams.get("q") || ""
  const patientNameFromUrl = queryParams.get("patientName") || ""

  const clinicId =
    (activeClinicId && activeClinicId !== "ALL" ? activeClinicId : "") ||
    (!activeClinicId && clinicIdFromUrl && clinicIdFromUrl !== "ALL" ? clinicIdFromUrl : "") ||
    (!activeClinicId && localStorage.getItem("odontoflow_clinic_id") !== "ALL"
      ? localStorage.getItem("odontoflow_clinic_id") || ""
      : "")

  const isAllClinicsContext = activeClinicId === "ALL"

  const {
    openPlan,
    openPatientById,
    openFinanceById,
    scheduleFromPlan
  } = useTreatmentPlanNavigation({
    clinicId,
    navigate
  })

  const [statusFilter, setStatusFilter] = useState("ALL")
  const [financialFilter, setFinancialFilter] = useState("ALL")
  const [quickFilter, setQuickFilter] = useState("ALL")

  const [selectedPatientId, setSelectedPatientId] = useState(patientIdFromUrl || "")
  const [selectedPatientName, setSelectedPatientName] = useState(patientNameFromUrl || "")

  useEffect(() => {
    setSelectedPatientId(patientIdFromUrl || "")
    setSelectedPatientName(patientNameFromUrl || "")
  }, [patientIdFromUrl, patientNameFromUrl])

  const {
    loading,
    error,
    search,
    setSearch,
    plans,
    loadPlans,
    loadingTreatments,
    treatments,
    loadTreatments,
    loadingPatients,
    patientSearch,
    setPatientSearch,
    patients
  } = useTreatmentPlansData({
    clinicId,
    initialSearch: qFromUrl
  })

  const treatmentsSorted = useMemo(() => {
    return [...treatments].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), "es", {
        sensitivity: "base"
      })
    )
  }, [treatments])

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  const isPatientContext = Boolean(patientIdFromUrl)
  const isQueueMobile = viewportWidth < 980

  const effectivePatientId = patientIdFromUrl || ""
  const draftPatientId = patientIdFromUrl || selectedPatientId || ""

  const currentPatientMeta = useMemo(() => {
    if (!effectivePatientId) return null

    const fromPatients =
      patients.find((item) => String(item.id) === String(effectivePatientId)) || null

    if (fromPatients) return fromPatients

    const fromPlans =
      plans.find((plan) => String(plan.patientId || "") === String(effectivePatientId))
        ?.patient || null

    return fromPlans
  }, [effectivePatientId, patients, plans])

  const effectivePatientName = useMemo(() => {
    if (!effectivePatientId) return ""

    const fromUrl = String(patientNameFromUrl || "").trim()
    if (fromUrl) return fromUrl

    if (currentPatientMeta) {
      return `${currentPatientMeta.firstName || ""} ${currentPatientMeta.lastName || ""}`.trim()
    }

    return ""
  }, [effectivePatientId, patientNameFromUrl, currentPatientMeta])

  const filteredPlans = useMemo(
    () =>
      buildFilteredPlans({
        plans,
        effectivePatientId,
        statusFilter,
        financialFilter,
        quickFilter
      }),
    [plans, effectivePatientId, statusFilter, financialFilter, quickFilter]
  )

  const patientFocusedMetrics = useMemo(
    () => buildPatientFocusedMetrics(filteredPlans),
    [filteredPlans]
  )

  const summary = useMemo(
    () => buildPlansSummary(filteredPlans),
    [filteredPlans]
  )

  const executiveInsights = useMemo(
    () => buildExecutiveInsights(filteredPlans),
    [filteredPlans]
  )

  const {
    createModeOpen,
    setCreateModeOpen,
    creatingPlan,
    planHeader,
    setPlanHeader,
    itemDraft,
    setItemDraft,
    planItems,
    showQuickTreatmentForm,
    setShowQuickTreatmentForm,
    savingQuickTreatment,
    quickTreatmentForm,
    setQuickTreatmentForm,
    planDraftTotal,
    addDraftItem,
    removeDraftItem,
    clearDraft,
    handleQuickCreateTreatment,
    createManualPlan
  } = useTreatmentPlanDraft({
    clinicId,
    patientIdFromUrl,
    effectivePatientId: draftPatientId,
    treatmentsSorted,
    loadTreatments,
    loadPlans,
    setSelectedPatientId
  })

  const displayDraftPatientName = useMemo(() => {
    if (patientIdFromUrl) return effectivePatientName

    const fromState = String(selectedPatientName || "").trim()
    if (fromState) return fromState

    const found = patients.find((item) => String(item.id) === String(draftPatientId))
    if (found) {
      return `${found.firstName || ""} ${found.lastName || ""}`.trim()
    }

    return ""
  }, [
    patientIdFromUrl,
    effectivePatientName,
    selectedPatientName,
    patients,
    draftPatientId
  ])

  const doctorIdsInPlans = useMemo(() => {
    const ids = new Set()

    filteredPlans.forEach((plan) => {
      ;(plan.items || []).forEach((item) => {
        if (item?.assignedDoctorId) ids.add(String(item.assignedDoctorId))
        if (item?.suggestedDoctorId) ids.add(String(item.suggestedDoctorId))
        if (item?.assignedDoctor?.id) ids.add(String(item.assignedDoctor.id))
        if (item?.suggestedDoctor?.id) ids.add(String(item.suggestedDoctor.id))
        if (item?.appointment?.doctor?.id) ids.add(String(item.appointment.doctor.id))
      })
    })

    return ids
  }, [filteredPlans])

  const hasMultipleDoctorsInContext = doctorIdsInPlans.size > 1

  const handleToggleCreateMode = () => {
    if (createModeOpen) {
      setCreateModeOpen(false)
      return
    }

    if (!patientIdFromUrl) {
      setSelectedPatientId("")
      setSelectedPatientName("")
    }

    setCreateModeOpen(true)
  }

  const resetPatientContext = () => {
    navigate("/treatment-plans")
  }

  const mobileQueueCards = useMemo(() => {
    return filteredPlans.map((plan) => {
      const nextItem = findNextSchedulableItem(plan)
      const items = Array.isArray(plan.items) ? plan.items : []
      const pendingBalance = getPendingBalance(plan)
      const planPatientName = getPlanPatientName(plan)

      const subtitle = isPatientContext
        ? `${items.length} ítem(s) · ${formatDateTime(plan.createdAt)}`
        : `${planPatientName} · ${items.length} ítem(s)`

      let pendingTone = "success"
      if (pendingBalance > 0 && pendingBalance < 3000) pendingTone = "info"
      else if (pendingBalance >= 3000 && pendingBalance < 10000) pendingTone = "warning"
      else if (pendingBalance >= 10000) pendingTone = "danger"

      let background = "#ffffff"
      let borderColor = "#e2e8f0"

      if (String(plan.status || "").toUpperCase() === "COMPLETED") {
        background = "#f8fffb"
        borderColor = "#bbf7d0"
      } else if (pendingBalance > 0) {
        background = "#fffdf7"
        borderColor = "#fde68a"
      }

      return {
        id: plan.id,
        title: plan.title || "Plan de tratamiento",
        subtitle,
        status: plan.status,
        totalEstimated: formatMoney(plan.totalEstimated || 0),
        totalPaid: formatMoney(plan.totalPaid || 0),
        totalProduced: formatMoney(plan.totalProduced || 0),
        pendingBalance: formatMoney(pendingBalance),
        pendingTone,
        background,
        borderColor,
        nextActionTitle: nextItem
          ? nextItem.treatment?.name || "Agendar siguiente tratamiento"
          : pendingBalance > 0
            ? "Dar seguimiento financiero"
            : "Sin acción inmediata",
        nextActionSubtitle: nextItem
          ? `${nextItem.tooth ? `Pieza ${nextItem.tooth} · ` : ""}${nextItem.surface ? `Superficie ${nextItem.surface} · ` : ""}${nextItem.condition || "Pendiente por ejecutar"}`
          : pendingBalance > 0
            ? "El plan sigue con saldo pendiente."
            : "No hay ítems pendientes por ejecutar.",
        onOpenPlan: () => openPlan(plan),
        onSchedule: nextItem ? () => scheduleFromPlan(plan) : null,
        onOpenPatient: !isPatientContext && plan.patientId
          ? () => openPatientById(plan.patientId)
          : null,
        onOpenFinance: plan.patientId
          ? () => openFinanceById(plan.patientId, "RECEIVABLES")
          : null
      }
    })
  }, [
    filteredPlans,
    isPatientContext,
    openPlan,
    scheduleFromPlan,
    openPatientById,
    openFinanceById
  ])

  const clearFilters = () => {
    setStatusFilter("ALL")
    setFinancialFilter("ALL")
    setQuickFilter("ALL")
    setSearch("")
  }

  const tableHeaders = useMemo(() => {
    return isPatientContext
      ? ["Plan", "Estado", "Próximo paso", "Saldo", "Acciones"]
      : ["Paciente", "Plan", "Estado", "Próximo paso", "Saldo", "Acciones"]
  }, [isPatientContext])

  const rowsForTable = useMemo(() => {
    return filteredPlans.map((plan) => {
      const pendingBalance = getPendingBalance(plan)
      const nextItem = findNextSchedulableItem(plan)
      const nextAction = buildNextAction(plan)
      const status = String(plan.status || "").toUpperCase()
      const items = Array.isArray(plan.items) ? plan.items : []

      let rowTone = "normal"
      if (status === "COMPLETED") rowTone = "success"
      else if (pendingBalance > 0 || nextItem) rowTone = "warning"

      const patientCell = (
        <div>
          <div style={{ fontWeight: 800, color: "#0f172a" }}>
            {getPlanPatientName(plan)}
          </div>
          <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 4 }}>
            {plan.patient?.phone || plan.patient?.email || "Sin contacto"}
          </div>
        </div>
      )

      const planCell = (
        <div>
          <div style={{ fontWeight: 900, color: "#0f172a", lineHeight: 1.2 }}>
            {plan.title || "Plan de tratamiento"}
          </div>
          <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 4, lineHeight: 1.35 }}>
            {items.length} ítem(s) · Creado {formatDateTime(plan.createdAt)}
          </div>
        </div>
      )

      const statusCell = (
        <div>
          <StatusBadge status={plan.status} />
          <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 6 }}>
            {status === "COMPLETED"
              ? "Sin pendientes"
              : pendingBalance > 0
                ? "Con saldo pendiente"
                : "En seguimiento"}
          </div>
        </div>
      )

      const nextActionCell = (
        <div>
          <div style={{ fontWeight: 800, color: "#0f172a" }}>{nextAction.title}</div>
          <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 4 }}>
            {nextAction.helper}
          </div>

          {hasMultipleDoctorsInContext && nextItem?.suggestedDoctor?.name ? (
            <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 6 }}>
              Doctor sugerido: <strong>{nextItem.suggestedDoctor.name}</strong>
            </div>
          ) : null}
        </div>
      )

      const balanceCell = (
        <div>
          <div
            style={{
              fontWeight: 900,
              color: pendingBalance > 0 ? "#b45309" : "#166534",
              fontSize: 15
            }}
          >
            {formatMoney(pendingBalance)}
          </div>
          <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 4 }}>
            {pendingBalance > 0 ? "Pendiente" : "Cubierto"}
          </div>
        </div>
      )

      const actionsCell = (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={() => openPlan(plan)}
            style={{
              ...buttonStyle,
              background: "#ffffff"
            }}
          >
            Ver detalle
          </button>

          {nextItem ? (
            <button
              onClick={() => scheduleFromPlan(plan)}
              style={{
                ...buttonStyle,
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                color: "#1d4ed8"
              }}
            >
              Agendar siguiente
            </button>
          ) : null}

          {!isPatientContext && plan.patientId ? (
            <button
              onClick={() => openPatientById(plan.patientId)}
              style={{
                ...buttonStyle,
                background: "#f8fafc"
              }}
            >
              Paciente
            </button>
          ) : null}
        </div>
      )

      const cells = isPatientContext
        ? [planCell, statusCell, nextActionCell, balanceCell, actionsCell]
        : [patientCell, planCell, statusCell, nextActionCell, balanceCell, actionsCell]

      return {
        rowTone,
        cells
      }
    })
  }, [
    filteredPlans,
    isPatientContext,
    hasMultipleDoctorsInContext,
    openPlan,
    scheduleFromPlan,
    openPatientById
  ])

  return (
    <div style={{ padding: 20, background: "#f8fafc", minHeight: "100vh", display: "grid", gap: 16 }}>
      <PlanModuleHero
        isPatientContext={isPatientContext}
        effectivePatientId={effectivePatientId}
        effectivePatientName={effectivePatientName}
        currentPatientMeta={currentPatientMeta}
        patientFocusedMetrics={patientFocusedMetrics}
        summary={summary}
        executiveInsights={executiveInsights}
        formatMoney={formatMoney}
        createModeOpen={createModeOpen}
        onToggleCreateMode={handleToggleCreateMode}
        onResetPatientContext={resetPatientContext}
        onOpenPatient={openPatientById}
        onOpenFinance={openFinanceById}
      />

      {!clinicId ? (
        <div
          style={{
            border: "1px solid #fecaca",
            background: "#fef2f2",
            color: "#991b1b",
            borderRadius: 16,
            padding: 16,
            boxShadow: "0 8px 22px rgba(15,23,42,0.04)"
          }}
        >
          {isAllClinicsContext
            ? "Planes trabaja por clínica. Selecciona una clínica específica para revisar o crear planes de tratamiento."
            : "No hay una clínica válida seleccionada."}
        </div>
      ) : (
        <>
          <PlanDraftBuilder
            createModeOpen={createModeOpen}
            onClose={() => setCreateModeOpen(false)}
            patientIdFromUrl={patientIdFromUrl}
            selectedPatientId={draftPatientId}
            selectedPatientName={displayDraftPatientName}
            loadingPatients={loadingPatients}
            patientSearch={patientSearch}
            setPatientSearch={setPatientSearch}
            patients={patients}
            setSelectedPatientId={setSelectedPatientId}
            setSelectedPatientName={setSelectedPatientName}
            planHeader={planHeader}
            setPlanHeader={setPlanHeader}
            planDraftTotal={planDraftTotal}
            formatMoney={formatMoney}
            showQuickTreatmentForm={showQuickTreatmentForm}
            setShowQuickTreatmentForm={setShowQuickTreatmentForm}
            quickTreatmentForm={quickTreatmentForm}
            setQuickTreatmentForm={setQuickTreatmentForm}
            handleQuickCreateTreatment={handleQuickCreateTreatment}
            savingQuickTreatment={savingQuickTreatment}
            itemDraft={itemDraft}
            setItemDraft={setItemDraft}
            loadingTreatments={loadingTreatments}
            treatmentsSorted={treatmentsSorted}
            addDraftItem={addDraftItem}
            planItems={planItems}
            removeDraftItem={removeDraftItem}
            createManualPlan={createManualPlan}
            creatingPlan={creatingPlan}
            clearDraft={clearDraft}
          />

          {loading ? (
            <div
              style={{
                border: "1px solid #e2e8f0",
                background: "#ffffff",
                color: "#64748b",
                borderRadius: 16,
                padding: 16,
                boxShadow: "0 8px 22px rgba(15,23,42,0.04)"
              }}
            >
              Cargando planes...
            </div>
          ) : error ? (
            <div
              style={{
                border: "1px solid #fecaca",
                background: "#fef2f2",
                color: "#991b1b",
                borderRadius: 16,
                padding: 16,
                boxShadow: "0 8px 22px rgba(15,23,42,0.04)"
              }}
            >
              {error}
            </div>
          ) : (
            <PlanQueueSection
              isMobile={isQueueMobile}
              mobileCards={
                <PlanQueueMobileList
                  cards={mobileQueueCards}
                  emptyLabel={
                    effectivePatientId
                      ? "Sin planes registrados para este paciente con los filtros actuales."
                      : "Sin planes registrados en esta clínica con los filtros actuales."
                  }
                />
              }
              effectivePatientId={effectivePatientId}
              search={search}
              setSearch={setSearch}
              clearFilters={clearFilters}
              quickFilter={quickFilter}
              setQuickFilter={setQuickFilter}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              financialFilter={financialFilter}
              setFinancialFilter={setFinancialFilter}
              tableHeaders={tableHeaders}
              rowsForTable={rowsForTable}
            />
          )}
        </>
      )}
    </div>
  )
}